import { useTable, type RowData, type SortingState } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { useState, type KeyboardEvent, type MouseEvent } from 'react';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { IllustrationKey } from '@/config/illustrations';
import { cn } from '@/lib/utils';

import { dataTableFeatures, type DataTableColumn } from './dataTable.columns';
import { EmptyState } from './EmptyState';
import { LoadingState } from './LoadingState';
import { PaginationBar, type PaginationBarProps } from './PaginationBar';

/** 행 안의 인터랙티브 자손 — 여기서 시작한 클릭·키 입력은 행 이동으로 승격하지 않는다(컬럼 측 stopPropagation 불필요). */
const INTERACTIVE_SELECTOR =
  'a,button,input,select,textarea,[role="button"],[role="link"],[role="menuitem"],[role="checkbox"]';

function isFromInteractiveChild(e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) {
  const target = e.target as HTMLElement;
  return target !== e.currentTarget && target.closest(INTERACTIVE_SELECTOR) !== null;
}

export interface DataTableProps<TData extends RowData> {
  columns: DataTableColumn<TData>[];
  data: TData[];
  /** 행 식별자(기본 index). */
  getRowId?: (row: TData, index: number) => string;
  isLoading?: boolean;
  emptyTitle?: string;
  emptyDescription?: React.ReactNode;
  /** 빈 상태 일러스트 키(미지정 시 기본 아이콘). */
  emptyIllustration?: IllustrationKey;
  /**
   * 행 활성화(상세 진입) — 마우스 클릭 + 키보드(행 포커스 후 Enter/Space). 지정 시 행이 tabIndex=0 포커스 스톱이 된다.
   * 행의 `row` role 은 유지한다(button/link 로 덮으면 스크린리더의 표 구조·셀↔헤더 연계가 깨진다 — grid 패턴의 focusable row).
   * 행 안의 링크·버튼에서 시작한 클릭·Enter 는 그 요소 몫이며 행 이동을 일으키지 않는다.
   */
  onRowClick?: (row: TData) => void;
  rowClassName?: (row: TData) => string | undefined;
  /** 서버 페이징 바(옵션). */
  pagination?: PaginationBarProps;
  /** 초기 정렬. */
  initialSorting?: SortingState;
  className?: string;
}

/**
 * 데이터 테이블 — Admin Kit Table + TanStack v9(headless). 로딩·빈 상태·정렬·행 클릭·서버 페이징 내장.
 * 셀·머리 규격은 Admin Kit 원본 그대로(첫·끝 열만 카드 패딩 24 에 맞춤), 정렬 머리는 원본 data-table-column-header 의 ghost 버튼 형태.
 * 정렬 머리·클릭 행 모두 키보드로 조작 가능(jsx-a11y 가 표 래퍼를 검사하지 못하므로 여기가 유일한 방어선).
 * ATable 대체. 셀 렌더는 컬럼 정의(`cell`)가 소유하고, 테이블은 배치만 담당한다.
 */
export function DataTable<TData extends RowData>({
  columns,
  data,
  getRowId,
  isLoading,
  emptyTitle = '표시할 항목이 없습니다',
  emptyDescription,
  emptyIllustration,
  onRowClick,
  rowClassName,
  pagination,
  initialSorting = [],
  className,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const table = useTable({
    features: dataTableFeatures,
    columns,
    data,
    getRowId,
    state: { sorting },
    onSortingChange: setSorting,
  });

  const colCount = columns.length;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((group) => (
              <TableRow key={group.id} className="hover:bg-transparent">
                {group.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <TableHead
                      key={header.id}
                      className="whitespace-nowrap first:pl-6 last:pr-6"
                      aria-sort={
                        sorted === 'asc'
                          ? 'ascending'
                          : sorted === 'desc'
                            ? 'descending'
                            : undefined
                      }
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        // 정렬은 실제 버튼 — 키보드(Tab → Enter/Space)로 토글, aria-sort 는 th 가 유지
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <table.FlexRender header={header} />
                          {sorted === 'asc' ? (
                            <ArrowUp className="size-4" aria-hidden="true" />
                          ) : sorted === 'desc' ? (
                            <ArrowDown className="size-4" aria-hidden="true" />
                          ) : (
                            <ArrowUpDown className="size-4 opacity-40" aria-hidden="true" />
                          )}
                        </Button>
                      ) : (
                        <table.FlexRender header={header} />
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={colCount} className="p-0">
                  <LoadingState />
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={colCount} className="p-0">
                  <EmptyState
                    compact
                    title={emptyTitle}
                    description={emptyDescription}
                    illustration={emptyIllustration}
                  />
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  tabIndex={onRowClick ? 0 : undefined}
                  className={cn(
                    onRowClick &&
                      'cursor-pointer focus-visible:bg-muted/50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
                    rowClassName?.(row.original),
                  )}
                  onClick={
                    onRowClick
                      ? (e) => {
                          if (!isFromInteractiveChild(e)) onRowClick(row.original);
                        }
                      : undefined
                  }
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.target !== e.currentTarget) return; // 셀 내부 요소의 키 입력은 그 요소 몫
                          if (e.key !== 'Enter' && e.key !== ' ') return;
                          e.preventDefault(); // Space 의 페이지 스크롤 차단
                          onRowClick(row.original);
                        }
                      : undefined
                  }
                >
                  {row.getAllCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      // 한글 제목이 좁은 카드에서 단어 단위로 줄바꿈되도록(nowrap 은 셀 렌더가 개별 지정)
                      className="break-keep whitespace-normal first:pl-6 last:pr-6"
                    >
                      <table.FlexRender cell={cell} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {pagination && !isLoading && <PaginationBar {...pagination} />}
    </div>
  );
}
